import { useQuery } from "@tanstack/react-query";
import { getSupabase, getSupabaseWithLogging, handleSupabaseError } from "@/lib/hooks/api/utils";
import { buildContactsQuery } from "@wtc/supabase";
import type { Contacts } from "@/src/gql/graphql";

export function useClientSearch(searchTerm: string) {
  return useQuery({
    queryKey: ["contacts", "fts", { search: searchTerm }],
    queryFn: async () => {
      const trimmed = searchTerm?.trim() || "";
      if (trimmed.length < 2) return [];

      const supabase = getSupabase();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("Not authenticated");
      }

      const searchResponse = await fetch("/api/search/contacts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ query: trimmed, limit: 50, offset: 0 }),
      });
      if (!searchResponse.ok) {
        throw new Error(`Search failed: ${searchResponse.statusText}`);
      }
      const searchData = await searchResponse.json();
      if (!searchData.success) {
        throw new Error(searchData.error || "Search failed");
      }
      const ftsResults = searchData.data || [];
      if (ftsResults.length === 0) return [];

      const ids = ftsResults.map((r: { id: string }) => r.id);
      const byIdsResponse = await fetch("/api/entities/contacts/by-ids", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ ids, ttl: 3600 }),
      });
      if (!byIdsResponse.ok) {
        throw new Error(`Failed to fetch contacts: ${byIdsResponse.statusText}`);
      }
      const byIdsData = await byIdsResponse.json();
      if (!byIdsData.success) {
        throw new Error(byIdsData.error || "Failed to fetch contacts");
      }
      const fullRows = byIdsData.data || [];

      const ordered = ids
        .map((id: string) => fullRows.find((r: { id: string }) => r.id === id))
        .filter(Boolean) as Contacts[];
      return ordered.filter((c) => c.is_active !== false);
    },
    enabled: !!searchTerm && searchTerm.trim().length > 1,
    staleTime: 5 * 60 * 1000,
  });
}

export function useContactsByIds(contactIds: string[]) {
  return useQuery({
    queryKey: ["contacts-by-ids", contactIds],
    queryFn: async () => {
      if (contactIds.length === 0) return [];

      const supabase = getSupabaseWithLogging();
      const { data, error } = await supabase.from("contacts").select("*").in("id", contactIds);

      if (error) {
        handleSupabaseError(error, "Failed to fetch contacts by IDs");
        return [];
      }

      return data || [];
    },
    enabled: contactIds.length > 0,
  });
}

export function useContacts(params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: "lead" | "prospect" | "customer" | "churned" | "inactive";
  contactType?: "individual" | "company";
  lifecycleStage?: string;
  source?: string;
  isActive?: boolean;
  tags?: string[];
}) {
  return useQuery({
    queryKey: ["contacts", params],
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      console.log("🔍 useContacts: queryFn start", { params });
      const supabase = getSupabase();

      const query = buildContactsQuery(supabase, {
        status: params?.status,
        contactType: params?.contactType,
        isActive: params?.isActive,
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
      });

      const { data, error } = await query;

      if (error) {
        console.error("Error fetching contacts:", {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code,
          error: error,
        });
        throw new Error(error.message || "Failed to fetch contacts");
      }

      const result = data || [];
      console.log("✅ useContacts: queryFn success", { count: result.length });
      return result;
    },
  });
}

// Delegates to useContacts with status="customer" for backward compatibility
export function useClients(params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}) {
  return useContacts({ ...params, status: "customer" });
}

// Get customers only (filtered contacts with status = 'customer')
export function useCustomers(params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}) {
  return useContacts({ ...params, status: "customer" });
}

export function useContact(id: string | null | undefined, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["contacts", id],
    queryFn: async () => {
      const supabase = getSupabase();
      const { data, error } = await supabase.from("contacts").select("*").eq("id", id).single();

      if (error) {
        console.error("Error fetching contact:", error);
        throw new Error(error.message);
      }

      return data;
    },
    enabled: options?.enabled !== false && !!id,
    staleTime: 5 * 60 * 1000,
  });
}
